import { useLayoutEffect, type RefObject } from 'react';

const DEFAULT_ACTIVE_SELECTOR = '[data-scroll-active="true"]';
const ACTIVE_ATTRIBUTE_FILTER = ['data-state', 'data-scroll-active'];

export interface UseScrollActiveChildIntoViewOptions {
    /** When false, scrolling is skipped entirely. Defaults to true. */
    enabled?: boolean;
    /** Re-scroll when active markers change (e.g. Radix tab clicks). Defaults to false. */
    watchActiveChanges?: boolean;
}

function scrollActiveChildIntoView(container: HTMLElement, activeSelector: string): void {
    const active = container.querySelector(activeSelector);
    if (!(active instanceof HTMLElement)) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();

    if (activeRect.left < containerRect.left) {
        container.scrollLeft -= containerRect.left - activeRect.left;
        return;
    }

    if (activeRect.right > containerRect.right) {
        container.scrollLeft += activeRect.right - containerRect.right;
    }
}

/**
 * Scrolls the first descendant matching `activeSelector` into view inside a horizontal scroll container.
 * Intended for pill/tab navigations where the active item must stay fully visible.
 * Uses `scrollLeft` so only the list pans — never the document.
 */
export function useScrollActiveChildIntoView<T extends HTMLElement>(
    containerRef: RefObject<T | null>,
    activeSelector: string = DEFAULT_ACTIVE_SELECTOR,
    { enabled = true, watchActiveChanges = false }: UseScrollActiveChildIntoViewOptions = {}
): void {
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || !enabled) return;

        scrollActiveChildIntoView(container, activeSelector);
    }, [containerRef, activeSelector, enabled]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || !enabled) return;

        const observer = new ResizeObserver(() => {
            scrollActiveChildIntoView(container, activeSelector);
        });
        observer.observe(container);

        return () => observer.disconnect();
    }, [containerRef, activeSelector, enabled]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || !enabled || !watchActiveChanges) return;

        const observer = new MutationObserver(() => {
            scrollActiveChildIntoView(container, activeSelector);
        });
        observer.observe(container, {
            subtree: true,
            attributes: true,
            attributeFilter: ACTIVE_ATTRIBUTE_FILTER,
        });

        return () => observer.disconnect();
    }, [containerRef, activeSelector, enabled, watchActiveChanges]);
}
